import {
  forwardRef,
  useMemo,
  useRef,
  type ChangeEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { Eraser, Search } from 'lucide-react';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { javascript } from '@codemirror/lang-javascript';
import { StreamLanguage, HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import { openSearchPanel, search } from '@codemirror/search';
import { searchMatchCount } from './searchMatchCount';
import { groovy } from '@codemirror/legacy-modes/mode/groovy';
import { properties } from '@codemirror/legacy-modes/mode/properties';
import { tags } from '@lezer/highlight';
import { useTheme } from '../../contexts/ThemeContext';

type CodeLanguage =
  | 'csv'
  | 'groovy'
  | 'javascript'
  | 'json'
  | 'properties'
  | 'regex'
  | 'text'
  | 'xml';

interface Props
  extends Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'defaultValue' | 'onChange' | 'value'
  > {
  value?: string;
  defaultValue?: string;
  language?: CodeLanguage;
  fullHeight?: boolean;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
}

const monoFont =
  'JetBrains Mono, Fira Code, Menlo, Monaco, Consolas, monospace';

const lightSyntax = HighlightStyle.define([
  { tag: tags.keyword, color: '#7c3aed', fontWeight: '600' },
  { tag: tags.atom, color: '#2563eb' },
  { tag: tags.bool, color: '#2563eb' },
  { tag: tags.number, color: '#0f766e' },
  { tag: tags.string, color: '#15803d' },
  { tag: tags.special(tags.string), color: '#047857' },
  { tag: tags.comment, color: '#64748b', fontStyle: 'italic' },
  { tag: tags.variableName, color: '#1e293b' },
  { tag: tags.definition(tags.variableName), color: '#0369a1' },
  { tag: tags.function(tags.variableName), color: '#0f766e' },
  { tag: tags.propertyName, color: '#b45309' },
  { tag: tags.typeName, color: '#be123c' },
  { tag: tags.className, color: '#be123c' },
  { tag: tags.tagName, color: '#2563eb' },
  { tag: tags.attributeName, color: '#b45309' },
  { tag: tags.meta, color: '#0891b2' },
  { tag: tags.invalid, color: '#dc2626', textDecoration: 'underline' },
]);

const darkSyntax = HighlightStyle.define([
  { tag: tags.keyword, color: '#c084fc', fontWeight: '600' },
  { tag: tags.atom, color: '#93c5fd' },
  { tag: tags.bool, color: '#93c5fd' },
  { tag: tags.number, color: '#5eead4' },
  { tag: tags.string, color: '#86efac' },
  { tag: tags.special(tags.string), color: '#6ee7b7' },
  { tag: tags.comment, color: '#94a3b8', fontStyle: 'italic' },
  { tag: tags.variableName, color: '#e2e8f0' },
  { tag: tags.definition(tags.variableName), color: '#7dd3fc' },
  { tag: tags.function(tags.variableName), color: '#67e8f9' },
  { tag: tags.propertyName, color: '#fbbf24' },
  { tag: tags.typeName, color: '#fda4af' },
  { tag: tags.className, color: '#fda4af' },
  { tag: tags.tagName, color: '#7dd3fc' },
  { tag: tags.attributeName, color: '#fbbf24' },
  { tag: tags.meta, color: '#22d3ee' },
  { tag: tags.invalid, color: '#f87171', textDecoration: 'underline' },
]);

function detectLanguage(
  value: string,
  placeholder?: string,
  language?: CodeLanguage
) {
  if (language) return language;

  const source = value.trim() || placeholder?.trim() || '';
  if (source.startsWith('<')) return 'xml';
  if (
    (source.startsWith('{') && source.endsWith('}')) ||
    (source.startsWith('[') && source.endsWith(']'))
  ) {
    return 'json';
  }
  return 'text';
}

function languageExtension(language: CodeLanguage) {
  switch (language) {
    case 'groovy':
      return StreamLanguage.define(groovy);
    case 'javascript':
      return javascript();
    case 'json':
      return json();
    case 'xml':
      return xml();
    case 'properties':
      return StreamLanguage.define(properties);
    default:
      return [];
  }
}

const CodeArea = forwardRef<HTMLDivElement, Props>(function CodeArea(
  {
    className,
    defaultValue,
    disabled,
    fullHeight,
    language,
    onChange,
    placeholder,
    readOnly,
    value,
    ...rest
  },
  ref
) {
  const { theme } = useTheme();
  const source = value ?? defaultValue ?? '';
  const activeLanguage = detectLanguage(source, placeholder, language);

  const editorTheme = useMemo(
    () =>
      EditorView.theme(
        {
          '&': {
            height: '100%',
            minHeight: 'inherit',
            color: theme === 'dark' ? '#e2e8f0' : '#1e293b',
            backgroundColor: 'transparent',
            fontSize: '13px',
          },
          '&.cm-focused': {
            outline: 'none',
          },
          '.cm-scroller': {
            fontFamily: monoFont,
            lineHeight: '1.55',
            overflow: 'auto',
          },
          '.cm-content': {
            minHeight: 'inherit',
            padding: '8px 0',
            width: 'max-content',
            minWidth: '100%',
          },
          '.cm-line': {
            padding: '0 12px',
          },
          '.cm-gutters': {
            backgroundColor:
              theme === 'dark'
                ? 'rgba(6, 10, 23, 0.85)'
                : 'rgba(248, 250, 252, 0.9)',
            borderRight:
              theme === 'dark'
                ? '1px solid rgba(31, 46, 82, 0.7)'
                : '1px solid rgba(203, 213, 225, 0.9)',
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
            minHeight: '100%',
          },
          '.cm-activeLine': {
            backgroundColor:
              theme === 'dark'
                ? 'rgba(56, 189, 248, 0.08)'
                : 'rgba(14, 165, 233, 0.08)',
          },
          '.cm-activeLineGutter': {
            backgroundColor:
              theme === 'dark'
                ? 'rgba(56, 189, 248, 0.12)'
                : 'rgba(14, 165, 233, 0.12)',
            color: theme === 'dark' ? '#cbd5e1' : '#334155',
          },
          '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
            backgroundColor:
              theme === 'dark'
                ? 'rgba(56, 189, 248, 0.24)'
                : 'rgba(14, 165, 233, 0.18)',
          },
          '.cm-cursor': {
            borderLeftColor: '#38bdf8',
          },
          '.cm-placeholder': {
            color: theme === 'dark' ? '#64748b' : '#94a3b8',
          },
          '.cm-foldPlaceholder': {
            backgroundColor:
              theme === 'dark'
                ? 'rgba(31, 46, 82, 0.8)'
                : 'rgba(226, 232, 240, 0.9)',
            borderColor: theme === 'dark' ? '#2b3e6b' : '#cbd5e1',
            color: theme === 'dark' ? '#cbd5e1' : '#475569',
          },
          // Find/replace panel — CodeMirror ships it unstyled.
          '.cm-panels': {
            backgroundColor: theme === 'dark' ? '#0b1224' : '#f8fafc',
            color: theme === 'dark' ? '#e2e8f0' : '#1e293b',
          },
          '.cm-panels.cm-panels-top': {
            borderBottom:
              theme === 'dark'
                ? '1px solid rgba(31, 46, 82, 0.9)'
                : '1px solid #cbd5e1',
          },
          '.cm-panel.cm-search': {
            // Extra right padding keeps wrapped controls clear of the
            // absolutely positioned close button.
            padding: '8px 30px 8px 10px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            fontFamily: 'inherit',
            fontSize: '12px',
          },
          '.cm-panel.cm-search label': {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: theme === 'dark' ? '#94a3b8' : '#475569',
          },
          '.cm-panel.cm-search input.cm-textfield': {
            backgroundColor: theme === 'dark' ? 'rgba(2, 6, 23, 0.8)' : '#ffffff',
            border:
              theme === 'dark'
                ? '1px solid rgba(31, 46, 82, 0.9)'
                : '1px solid #cbd5e1',
            borderRadius: '6px',
            color: theme === 'dark' ? '#e2e8f0' : '#1e293b',
            padding: '4px 8px',
            fontSize: '12px',
          },
          '.cm-panel.cm-search input.cm-textfield:focus': {
            outline: 'none',
            borderColor: '#38bdf8',
          },
          '.cm-panel.cm-search button.cm-button': {
            backgroundColor: theme === 'dark' ? 'rgba(31, 46, 82, 0.6)' : '#e2e8f0',
            backgroundImage: 'none',
            border:
              theme === 'dark'
                ? '1px solid rgba(31, 46, 82, 0.9)'
                : '1px solid #cbd5e1',
            borderRadius: '6px',
            color: theme === 'dark' ? '#cbd5e1' : '#334155',
            padding: '3px 9px',
            fontSize: '12px',
            cursor: 'pointer',
          },
          '.cm-panel.cm-search button.cm-button:hover': {
            backgroundColor: theme === 'dark' ? 'rgba(56, 189, 248, 0.18)' : '#cbd5e1',
          },
          '.cm-panel.cm-search button[name="close"]': {
            color: theme === 'dark' ? '#94a3b8' : '#475569',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '0 4px',
          },
          '.cm-search-count': {
            minWidth: '76px',
            fontVariantNumeric: 'tabular-nums',
            fontSize: '11px',
            color: theme === 'dark' ? '#94a3b8' : '#64748b',
            whiteSpace: 'nowrap',
          },
          '.cm-searchMatch': {
            backgroundColor: 'rgba(250, 204, 21, 0.28)',
            outline: '1px solid rgba(250, 204, 21, 0.5)',
          },
          '.cm-searchMatch.cm-searchMatch-selected': {
            backgroundColor: 'rgba(56, 189, 248, 0.55)',
            outline: '1px solid #38bdf8',
          },
        },
        { dark: theme === 'dark' }
      ),
    [theme]
  );

  const extensions = useMemo(() => {
    const base = [
      languageExtension(activeLanguage),
      editorTheme,
      syntaxHighlighting(theme === 'dark' ? darkSyntax : lightSyntax),
      // The browser's own Ctrl+F cannot see this document: CodeMirror only
      // renders the lines currently in view, so anything scrolled away is not
      // in the DOM. This panel searches the whole document instead.
      search({ top: true }),
      searchMatchCount,
    ];
    if (readOnly || disabled) {
      // A non-editable view is not focusable, which would leave output panels
      // unreachable by keyboard — and unable to receive Ctrl+F at all.
      base.push(EditorView.contentAttributes.of({ tabindex: '0' }));
    }
    return base;
  }, [activeLanguage, editorTheme, theme, readOnly, disabled]);

  const cmRef = useRef<ReactCodeMirrorRef>(null);

  const handleSearch = () => {
    const view = cmRef.current?.view;
    if (!view) return;
    view.focus();
    openSearchPanel(view);
  };

  // Emptying the document through a transaction (rather than calling onChange
  // directly) keeps CodeMirror and React in step and leaves the clear on the
  // undo stack, so Ctrl+Z brings the payload back.
  const handleClear = () => {
    const view = cmRef.current?.view;
    if (!view) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: '' },
    });
    view.focus();
  };

  const editable = !readOnly && !disabled;

  return (
    <div
      ref={ref}
      className={`code-editor relative ${fullHeight ? 'h-full min-h-[200px]' : 'min-h-[180px]'} ${
        className ?? ''
      }`}
    >
      <div className="editor-actions">
        {editable && source.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="editor-action-btn editor-clear-btn"
            title="Clear this editor"
            aria-label="Clear editor"
          >
            <Eraser className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={handleSearch}
          className="editor-action-btn editor-search-btn"
          title="Search in editor (Ctrl+F)"
          aria-label="Search in editor"
        >
          <Search className="w-3.5 h-3.5" />
        </button>
      </div>
      <CodeMirror
        ref={cmRef}
        value={source}
        placeholder={placeholder}
        readOnly={readOnly || disabled}
        editable={!readOnly && !disabled}
        height="100%"
        minHeight={fullHeight ? '200px' : '180px'}
        theme="none"
        basicSetup={{
          autocompletion: true,
          bracketMatching: true,
          closeBrackets: true,
          foldGutter: true,
          highlightActiveLine: true,
          highlightActiveLineGutter: true,
          highlightSelectionMatches: true,
          indentOnInput: true,
          lineNumbers: true,
          searchKeymap: true,
        }}
        extensions={extensions}
        indentWithTab
        onChange={(nextValue) => {
          onChange?.({
            target: { value: nextValue },
            currentTarget: { value: nextValue },
          } as ChangeEvent<HTMLTextAreaElement>);
        }}
        aria-label={rest['aria-label']}
      />
    </div>
  );
});

export default CodeArea;
